// UmpirePolls — the umpire's landing page inside the Umpire workspace.
//
// Shows every open poll they can vote on (plus any match they're
// already assigned to, so they can see confirmed picks). Each card has
// a single button:
//   Not nominated → "I'm available"    (POST /polls/:id/mine)
//   Nominated     → "Withdraw"         (DELETE /polls/:id/mine)
//   Assigned      → "You're officiating" (read-only)

import React, { useEffect, useMemo, useState } from "react";
import {
  Gavel, MapPin, Clock, CheckCircle2, Ban, Sparkles, Trophy
} from "lucide-react";
import { Panel } from "../components/primitives";
import { useToast } from "../components/Toast";
import { api } from "../api";
import { fmtMatchDate, fmt12 } from "./league/shared";

export default function UmpirePolls() {
  const toast = useToast();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId,  setBusyId]  = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const d = await api.listMyPolls();
      setData(d);
    } catch (err) {
      toast.error(err.message || "Couldn't load polls.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  async function nominate(matchId) {
    setBusyId(matchId);
    try {
      await api.selfNominate(matchId);
      toast.success("You're on the poll — good luck!");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Couldn't submit your response.");
    } finally { setBusyId(null); }
  }

  async function withdraw(matchId) {
    if (!confirm("Withdraw your availability for this match?")) return;
    setBusyId(matchId);
    try {
      await api.withdrawNomination(matchId);
      toast.success("Withdrawn.");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Couldn't withdraw.");
    } finally { setBusyId(null); }
  }

  const polls = data?.polls || [];
  const openPolls   = useMemo(() => polls.filter(p => p.pollOpen), [polls]);
  const assigned    = useMemo(() => polls.filter(p => p.myStatus === "Assigned"), [polls]);

  const firstName = data?.umpire?.firstName || "";

  return (
    <div className="stack league-view">
      <div className="builderTop">
        <div>
          <p>Umpire Workspace</p>
          <h2>My Polls{firstName ? ` · Welcome, ${firstName}` : ""}</h2>
        </div>
      </div>

      {loading && <p style={{color:"var(--muted)"}}>Loading polls…</p>}

      {!loading && assigned.length > 0 && (
        <Panel title={<><Sparkles size={18}/> Confirmed Matches</>}>
          <div className="ump-poll-list">
            {assigned.map(p => (
              <article key={p.id} className="ump-pollCard ump-pollCard--assigned">
                <PollHeader poll={p}/>
                <div className="ump-pollCard-status ump-pollCard-status--assigned">
                  <CheckCircle2 size={14}/> You're officiating this match
                </div>
              </article>
            ))}
          </div>
        </Panel>
      )}

      {!loading && (
        <Panel title={<><Gavel size={18}/> Open Polls · nominate yourself</>}>
          {openPolls.length === 0 && (
            <div className="league-annEmpty">
              <Gavel size={28}/>
              <p>No open polls right now. When the committee opens one, it'll show up here.</p>
            </div>
          )}

          <div className="ump-poll-list">
            {openPolls.map(p => {
              const busy = busyId === p.id;
              const mine = p.myStatus;
              return (
                <article
                  key={p.id}
                  className={`ump-pollCard${mine === "Nominated" ? " ump-pollCard--in" : ""}`}
                >
                  <PollHeader poll={p}/>
                  <div className="ump-pollCard-foot">
                    <span className="ump-pollCard-counts">
                      {p.nominatedCount} umpire{p.nominatedCount === 1 ? "" : "s"} nominated ·
                      {" "}{p.assignedCount}/2 assigned
                    </span>
                    {mine === "Assigned" ? (
                      <span className="ump-pollCard-status ump-pollCard-status--assigned">
                        <CheckCircle2 size={13}/> Officiating
                      </span>
                    ) : mine === "Nominated" ? (
                      <button
                        type="button"
                        className="ump-pollCard-btn ump-pollCard-btn--withdraw"
                        onClick={() => withdraw(p.id)}
                        disabled={busy}
                      >
                        <Ban size={13}/> {busy ? "Working…" : "Withdraw"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="ump-pollCard-btn ump-pollCard-btn--in"
                        onClick={() => nominate(p.id)}
                        disabled={busy}
                      >
                        <CheckCircle2 size={13}/> {busy ? "Submitting…" : "I'm available"}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </Panel>
      )}
    </div>
  );
}

function PollHeader({ poll }) {
  return (
    <>
      <header className="ump-pollCard-head">
        <div className="ump-pollCard-date">
          <b>{fmtMatchDate(poll.date)}</b>
          {poll.time && <small><Clock size={10}/> {fmt12(poll.time)} ET</small>}
        </div>
        {poll.series && (
          <div className="ump-pollCard-series">
            <Trophy size={10}/> {poll.series}
          </div>
        )}
      </header>
      <div className="ump-pollCard-teams">
        <b>{poll.teamOne}</b>
        <span>vs</span>
        <b>{poll.teamTwo}</b>
      </div>
      <div className="ump-pollCard-ground">
        <MapPin size={11}/> {poll.ground}
      </div>
    </>
  );
}
